import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { skuApi, templateApi } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Page, Button, Badge, Section, Loading } from "../../Components/UI/ui";
import { GATE, money, cap } from "../../lib/lab";
import SkuForm from "../Catalogue/SkuForm";

// A package is a catalogue item (kind: package) sold at one price whose
// contents are the test definitions linked to it (LabSku.templateIds).
// Contents are managed through the template link endpoints.
const Packages = () => {
  const { can } = useAuth();
  const { toast, templates, loadTemplates, invalidateSkus } = useApp();
  const navigate = useNavigate();
  const canWrite = can(...GATE.catalogueWrite);

  const [skus, setSkus] = useState([]);           // every SKU of the lab (packages + singles, active or not)
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res] = await Promise.all([skuApi.list({ limit: 200 }), loadTemplates()]);
      setSkus(res?.data || []);
    } catch (e) { toast.error(e); } finally { setLoading(false); }
  }, [toast, loadTemplates]);

  useEffect(() => { load(); }, [load]);

  const packages = useMemo(() => {
    const s = search.trim().toLowerCase();
    return skus
      .filter((k) => k.kind === "package")
      .filter((k) => showInactive || k.isActive !== false)
      .filter((k) => !s || k.name.toLowerCase().includes(s))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [skus, search, showInactive]);

  const pkg = skus.find((k) => k._id === selectedId) || null;
  useEffect(() => {
    if (!selectedId && packages.length) setSelectedId(packages[0]._id);
  }, [packages, selectedId]);

  // Cheapest single (non-package) SKU that sells a given template — used to
  // show what the contents would cost individually.
  const singlePriceFor = useCallback((templateId) => {
    const singles = skus.filter((k) => k.kind !== "package" && (k.templateIds || []).some((t) => String(t) === String(templateId)));
    if (!singles.length) return null;
    return singles.reduce((m, k) => (k.unitPrice < m.unitPrice ? k : m));
  }, [skus]);

  const contents = useMemo(() => {
    if (!pkg) return [];
    return (pkg.templateIds || []).map((id) => {
      const t = templates[String(id)];
      const single = singlePriceFor(id);
      return { id: String(id), t, single };
    });
  }, [pkg, templates, singlePriceFor]);

  const individualTotal = contents.reduce((sum, c) => sum + (c.single?.unitPrice || 0), 0);
  const unpriced = contents.filter((c) => !c.single).length;
  const inPkg = new Set(contents.map((c) => c.id));
  const addable = Object.values(templates).filter((t) => t.isActive !== false && !inPkg.has(t._id)).sort((a, b) => a.name.localeCompare(b.name));

  const patchSku = (id, patch) => setSkus((xs) => xs.map((k) => (k._id === id ? { ...k, ...patch } : k)));

  const addTemplate = async () => {
    if (!adding || !pkg) return;
    setBusy("add");
    try {
      await templateApi.linkSku(adding, pkg._id);
      patchSku(pkg._id, { templateIds: [...(pkg.templateIds || []), adding] });
      invalidateSkus();
      setAdding("");
      toast.success(`${templates[adding]?.name || "Test"} added to ${pkg.name}`);
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const removeTemplate = async (templateId) => {
    setBusy(templateId);
    try {
      await templateApi.unlinkSku(templateId, pkg._id);
      patchSku(pkg._id, { templateIds: (pkg.templateIds || []).filter((t) => String(t) !== templateId) });
      invalidateSkus();
      toast.success("Removed from package");
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const toggle = async () => {
    setBusy("toggle");
    try {
      const updated = await skuApi.toggle(pkg._id);
      patchSku(pkg._id, { isActive: updated?.isActive ?? !pkg.isActive });
      invalidateSkus();
      toast.success(updated?.isActive ?? !pkg.isActive ? "Package activated" : "Package deactivated");
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete package "${pkg.name}" permanently? Prefer deactivating.`)) return;
    setBusy("delete");
    try {
      await skuApi.remove(pkg._id);
      setSkus((xs) => xs.filter((k) => k._id !== pkg._id));
      setSelectedId(null);
      invalidateSkus();
      toast.success("Package deleted");
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const taxPct = (t) => (t?.cgst || 0) + (t?.sgst || 0) + (t?.igst || 0);

  return (
    <Page
      title="Packages"
      subtitle="Bundles of tests sold at one price"
      actions={canWrite && <Button variant="primary" onClick={() => { setEditing(null); setFormOpen(true); }}><IoMdAdd /> New package</Button>}
    >
      <div className="lab-page__body">
        {loading && skus.length === 0 ? <Loading /> : (
          <div className="lab-split">
            <div>
              <div className="lab-row" style={{ marginBottom: 10 }}>
                <input className="lab-input sm" style={{ flex: 1 }} placeholder="Search packages" value={search} onChange={(e) => setSearch(e.target.value)} />
                <label className="lab-check" style={{ fontSize: 12 }}><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Inactive</label>
              </div>
              {packages.length === 0 && <div className="lab-muted" style={{ padding: 12 }}>No packages yet. Create one, then add tests to it.</div>}
              {packages.map((k) => (
                <div key={k._id} className={`lab-pkg ${k._id === selectedId ? "selected" : ""}`} onClick={() => setSelectedId(k._id)}>
                  <div className="lab-row between">
                    <span className="name">{k.name}</span>
                    {k.isActive === false && <Badge kind="off">Inactive</Badge>}
                  </div>
                  <div className="meta">
                    <span>{(k.templateIds || []).length} test{(k.templateIds || []).length === 1 ? "" : "s"}{k.category ? ` · ${k.category}` : ""}</span>
                    <span>{money(k.unitPrice)}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {!pkg ? (
                <div className="lab-empty">Select a package to see its contents</div>
              ) : (
                <>
                  <Section
                    title={<span className="lab-row">{pkg.name} <Badge kind={pkg.isActive === false ? "off" : "on"}>{pkg.isActive === false ? "Inactive" : "Active"}</Badge> <Badge kind={pkg.serviceType}>{cap(pkg.serviceType)}</Badge></span>}
                    right={canWrite && (
                      <span className="lab-row">
                        <Button size="sm" onClick={() => { setEditing(pkg); setFormOpen(true); }}>Edit details</Button>
                        <Button size="sm" loading={busy === "toggle"} onClick={toggle}>{pkg.isActive === false ? "Activate" : "Deactivate"}</Button>
                        {can(...GATE.catalogueDelete) && <Button size="sm" variant="danger" loading={busy === "delete"} onClick={remove}>Delete</Button>}
                      </span>
                    )}
                  >
                    <div className="lab-grid cols-4">
                      <div><div className="lab-muted">Package price</div><div style={{ fontSize: 22, fontWeight: 600, color: "var(--navy)" }}>{money(pkg.unitPrice)}</div><div className="lab-muted">+ {taxPct(pkg.tax)}% tax</div></div>
                      <div><div className="lab-muted">Tests individually</div><div style={{ fontSize: 22, fontWeight: 600 }}>{money(individualTotal)}</div>{unpriced > 0 && <div className="lab-muted">{unpriced} not sold singly</div>}</div>
                      <div><div className="lab-muted">Patient saves</div><div style={{ fontSize: 22, fontWeight: 600, color: individualTotal > pkg.unitPrice ? "#1c6b3a" : "var(--muted)" }}>{individualTotal > pkg.unitPrice ? money(individualTotal - pkg.unitPrice) : "—"}</div>{individualTotal > 0 && individualTotal > pkg.unitPrice && <div className="lab-muted">{Math.round(((individualTotal - pkg.unitPrice) / individualTotal) * 100)}% off</div>}</div>
                      <div><div className="lab-muted">Code · TAT · collection</div><div>{pkg.labSkuId}</div><div className="lab-muted">{pkg.turnaroundHours ? `${pkg.turnaroundHours}h` : "—"} · {pkg.homeCollectionAvailable ? "home available" : "at centre"}</div></div>
                    </div>
                    {(pkg.preparationNotes || pkg.description) && (
                      <div className="lab-muted" style={{ marginTop: 10 }}>{pkg.preparationNotes}{pkg.preparationNotes && pkg.description ? " · " : ""}{pkg.description}</div>
                    )}
                  </Section>

                  <Section title={`Contents · ${contents.length} test${contents.length === 1 ? "" : "s"}`}>
                    {contents.length === 0 ? (
                      <div className="lab-muted">Empty package — bookings of it will produce no result sheets. Add tests below.</div>
                    ) : (
                      <table className="lab-lines">
                        <thead><tr><th>Test</th><th>Category</th><th className="num">Parameters</th><th className="num">Single price</th><th /></tr></thead>
                        <tbody>
                          {contents.map((c) => (
                            <tr key={c.id}>
                              <td>
                                {c.t ? (
                                  <button type="button" className="lab-link" style={{ fontWeight: 600, textDecoration: "none" }} onClick={() => navigate(`/tests/${c.id}`)}>{c.t.name}</button>
                                ) : <span className="lab-muted">Unknown definition {c.id}</span>}
                                {c.t?.shortName && <span className="lab-muted"> · {c.t.shortName}</span>}
                                {c.t?.isActive === false && <Badge kind="off" style={{ marginLeft: 6 }}>Inactive</Badge>}
                              </td>
                              <td className="lab-muted">{c.t?.category || "—"}</td>
                              <td className="num">{c.t?.parameterCount ?? "—"}</td>
                              <td className="num">{c.single ? <span title={c.single.name}>{money(c.single.unitPrice)}</span> : <span className="lab-muted">—</span>}</td>
                              <td>{canWrite && <Button size="xs" variant="ghost" loading={busy === c.id} onClick={() => removeTemplate(c.id)}>Remove</Button>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {canWrite && (
                      <div className="lab-row" style={{ marginTop: 12 }}>
                        <select className="lab-select" style={{ minWidth: 320 }} value={adding} onChange={(e) => setAdding(e.target.value)}>
                          <option value="">Add a test definition…</option>
                          {addable.map((t) => <option key={t._id} value={t._id}>{t.name}{t.shortName ? ` (${t.shortName})` : ""}{t.category ? ` · ${t.category}` : ""}</option>)}
                        </select>
                        <Button size="sm" variant="primary" disabled={!adding} loading={busy === "add"} onClick={addTemplate}>Add to package</Button>
                        {addable.length === 0 && <span className="lab-muted">Every active definition is already in this package.</span>}
                      </div>
                    )}
                  </Section>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <SkuForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        sku={editing}
        defaults={{ kind: "package", serviceType: "pathology" }}
        lockKind
        onSaved={(saved) => {
          if (editing) patchSku(editing._id, saved || {});
          else if (saved?._id) { setSkus((xs) => [...xs, saved]); setSelectedId(saved._id); }
          else load();
        }}
      />
    </Page>
  );
};

export default Packages;
